import React, { useState } from "react";
import { Box, Stack, Typography, TextField, Button, CircularProgress, Alert } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { QuizStyledCard } from "../../shared/QuizStyledCard";
import { PageTitle } from "../../shared/PageTitle";
import { COLORS } from "../constants";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  onSearchByEmail: (email: string) => void;
  onSearchById: (id: string) => void;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  onDismissSuccess: () => void;
}

export function CustomerSearchCard({ onSearchByEmail, onSearchById, loading, error, successMessage, onDismissSuccess }: Props) {
  const [searchEmail, setSearchEmail] = useState("");
  const [searchId, setSearchId] = useState("");

  return (
    <QuizStyledCard>
      <PageTitle title="Dev Tools" subtitle="Admin tools for managing customer subscriptions" />

      <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Admin Access Only</Typography>
        <Typography sx={{ fontSize: 13, opacity: 0.85 }}>
          This page allows direct modification of customer subscriptions. Use with caution.
        </Typography>
      </Alert>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={onDismissSuccess}>
          {successMessage}
        </Alert>
      )}

      <Stack spacing={3} sx={{ mt: 3 }}>
        <QuizStyledCard variant="white">
          <Typography sx={{ fontWeight: 700, color: COLORS.brownDeep, mb: 2 }}>
            Search by Email
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              placeholder="customer@email.com"
              fullWidth
              size="small"
              onKeyDown={(e) => e.key === "Enter" && onSearchByEmail(searchEmail)}
              sx={{ "& .MuiOutlinedInput-root": { bgcolor: QUIZ.color.warmWhite, borderRadius: "8px" } }}
            />
            <Button
              variant="contained"
              onClick={() => onSearchByEmail(searchEmail)}
              disabled={loading || !searchEmail.trim()}
              sx={{ bgcolor: COLORS.coral, "&:hover": { bgcolor: COLORS.coralDark }, textTransform: "none", fontWeight: 700, px: 3 }}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            >
              Search
            </Button>
          </Box>
        </QuizStyledCard>

        <QuizStyledCard variant="white">
          <Typography sx={{ fontWeight: 700, color: COLORS.brownDeep, mb: 2 }}>
            Search by Recharge Customer ID
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              placeholder="123456789"
              fullWidth
              size="small"
              onKeyDown={(e) => e.key === "Enter" && onSearchById(searchId)}
              sx={{ "& .MuiOutlinedInput-root": { bgcolor: QUIZ.color.warmWhite, borderRadius: "8px" } }}
            />
            <Button
              variant="contained"
              onClick={() => onSearchById(searchId)}
              disabled={loading || !searchId.trim()}
              sx={{ bgcolor: COLORS.brown, "&:hover": { bgcolor: COLORS.brownDark }, textTransform: "none", fontWeight: 700, px: 3 }}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            >
              Search
            </Button>
          </Box>
        </QuizStyledCard>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>
      )}
    </QuizStyledCard>
  );
}
